// ─── voicebox session config ────────────────────────────────────────────────
// What a caller supplies to `POST /v1/sessions`. This file is the schema AND its
// validator: shared with the server, any SDK, and the docs, so there is exactly
// one definition of "a valid session".
//
// Validation is hand-rolled on purpose — the shape is small, the error messages
// matter more than the machinery, and the wire contract stays dependency-free.

import {
  DEFAULT_AUDIO, type AudioFormat, type Placement, type TurnTaking, type VisualInputCapability,
} from './protocol.ts'

/** Smallest race-checked idle window accepted by the server's `/respond` route. */
export const IDLE_PROMPT_MIN_MS = 1_200

/** A JSON Schema object describing a tool's arguments. Passed through to the provider verbatim. */
export interface JsonSchemaObject {
  type: 'object'
  properties?: Record<string, unknown>
  required?: string[]
  [key: string]: unknown
}

/**
 * Where a tool actually runs:
 *  · `builtin` — voicebox implements it (see BUILTIN_TOOLS).
 *  · `webhook` — POSTed to the caller's HTTPS endpoint, HMAC-signed.
 *  · `client`  — forwarded down the socket for the SDK to execute.
 */
export type ToolExec = { kind: 'builtin' } | { kind: 'webhook'; url: string } | { kind: 'client' }

/** What the model should do after a tool result has been delivered. */
export type ToolAfter = 'continue' | 'stop'

export interface ToolDecl {
  name: string
  description: string
  parameters: JsonSchemaObject
  exec: ToolExec
  /** Dispatch deadline. Past it the tool returns a timeout error to the model. */
  timeoutMs: number
  /**
   * Most tools answer a question and the model should continue speaking. A terminal
   * tool represents the answer itself (for example, choosing to keep a silence), so
   * starting another response would undo the choice the tool just made.
   */
  after?: ToolAfter
}

export type WebhookEventKind = 'transcript' | 'tool' | 'session'

export interface WebhookConfig {
  url: string
  /** HMAC key for outgoing signatures. Falls back to the server's WEBHOOK_SIGNING_SECRET. */
  secret?: string
  events: WebhookEventKind[]
}

/** The "slow brain" behind the `deep_think` tool. All fields fall back to server env. */
export interface DeepThinkConfig {
  baseUrl?: string
  model?: string
  systemPrompt?: string
  timeoutMs: number
}

/** What a caller writes. */
export interface ProviderChoice {
  id: string
  /** Omit to take the provider's default placement. */
  placement?: Placement
  /** Provider-specific knobs — validated by the provider, not here. */
  options?: Record<string, unknown>
}

/** What validation returns: no optionals left for the server to re-derive. */
export interface ResolvedProvider {
  id: string
  placement: Placement
  options: Record<string, unknown>
}

export interface SessionConfig {
  /** System prompt / persona for the voice model. */
  persona: string
  provider: ResolvedProvider
  voice?: string
  locale?: string
  turnTaking: TurnTaking
  /** Spoken as soon as the client connects. */
  greeting?: string
  tools: ToolDecl[]
  webhook?: WebhookConfig
  deepThink: DeepThinkConfig
  audio: { input: AudioFormat; output: AudioFormat }
  /** Hard session lifetime; the session self-destructs at the end of it. */
  maxDurationMs: number
  /** Opaque caller-supplied string echoed back on every webhook. */
  ref?: string
}

/**
 * CI names every Worker secret after its `Env` key with this prefix, so one secret
 * name is enough to tell a caller both places to set it.
 * See `.github/workflows/deploy-voicebox-server.yml`.
 */
export const DEPLOY_SECRET_PREFIX = 'VOICEBOX_'

/** What the provider registry tells the validator about itself. */
export interface ProviderCatalogEntry {
  id: string
  /** Supported placements, most-preferred first (index 0 is the default). */
  placements: Placement[]
  status: 'ready' | 'planned'
  /** One-liner for `GET /v1/providers`. */
  summary?: string
  /** Worker secrets this provider needs. Empty means it runs with no credentials. */
  secrets?: string[]
  /** Whether those secrets are present. Absent when the catalog was built without env. */
  configured?: boolean
  missingSecrets?: string[]
  /** The formats this provider actually speaks, used when the caller declares none. */
  audio?: { input: AudioFormat; output: AudioFormat }
  /** Visual input supported by this provider, if any. */
  visual?: VisualInputCapability
}

export const DEFAULTS = {
  turnTaking: 'vad' as TurnTaking,
  maxDurationMs: 30 * 60_000,
  toolTimeoutMs: 15_000,
  deepThinkTimeoutMs: 60_000,
} as const

/**
 * How long silence has to last before the user's turn is over — the single largest
 * item in the end-of-turn latency budget for a LOCALLY GATED client, and a number
 * that MUST be agreed between two pieces of code that never talk to each other.
 *
 * In gated transmission, `turnTaking: 'vad'` runs TWO voice-activity detectors, and
 * only one of them answers. The provider's decides when the model replies. The
 * client's decides when the mic stops sending — and a provider that is no longer
 * being sent audio cannot finish counting silence. So the invariant is:
 *
 *     providerSilenceMs  <  clientHangoverMs
 *
 * Break it and turns do not get slower, they **stop**: measured against production on
 * 2026-07-31, DashScope's undeclared 800 ms default against the client's 700 ms gate
 * answered **0 of 6 turns**, each one hanging until the speaker said something else.
 * It survived in the wild only because a real room keeps an RMS gate open on breath
 * and background noise; a quiet room with a good microphone is the failing case.
 *
 * `providers/turn-detection.test.ts` asserts the inequality, because the two halves live
 * in different packages and nothing else would notice them drifting.
 *
 * **800 is not laziness, it is the measurement.** DashScope recommends 400 ms for
 * conversation and it is far too aggressive: driven against production with a real
 * mid-sentence pause, `server_vad` interrupts the speaker `silence - ~250 ms` into it,
 * because the window expires before the provider has registered that speech resumed.
 * Measured on 2026-07-31 against a 450 ms pause — 400 ms: cut off 4/4. 550 ms: 4/4.
 * 650 ms: 3/4. 800 ms against a 500 ms pause: 0/4. Every millisecond taken off this
 * number is bought by talking over somebody, which §17 is not worth.
 *
 * Native realtime clients should instead use continuous transmission and keep local
 * VAD only as an immediate UI/barge-in signal. WINDOW does that for Qwen 3.5 with
 * `semantic_vad`; these values remain the safe fallback for other gated consumers and
 * for the explicit `server_vad` option.
 */
export const TURN_END = {
  /** Silence the PROVIDER waits through before it commits the turn and replies. */
  providerSilenceMs: 800,
  /** Silence a GATED client's mic waits through before it stops sending frames. */
  clientHangoverMs: 1_000,
} as const

const MAX_TOOLS = 32
const MAX_PERSONA = 8000

/**
 * Tools voicebox implements itself. Callers declare only
 * `{ name: 'deep_think', exec: { kind: 'builtin' } }` and validation fills in the
 * description + schema, so the contract can't drift from the implementation.
 */
export const BUILTIN_TOOLS: Record<string, { description: string; parameters: JsonSchemaObject }> = {
  deep_think: {
    description:
      'Hand a hard question to a slower, stronger model and get its answer back. Use when the ' +
      'answer needs real reasoning, multi-step work, or care you cannot do while talking.',
    parameters: {
      type: 'object',
      properties: {
        question: { type: 'string', description: 'The question to think hard about.' },
        context: { type: 'string', description: 'Optional background from the conversation.' },
      },
      required: ['question'],
    },
  },
}

export type Validated<T> = { ok: true; value: T } | { ok: false; errors: string[] }

/**
 * Picks the provider and resolves its placement. Split out from
 * `validateSessionConfig` because placement resolution is the one rule worth
 * testing on its own.
 */
export function resolveProvider(
  choice: ProviderChoice,
  catalog: ProviderCatalogEntry[],
): Validated<{ id: string; placement: Placement }> {
  const entry = catalog.find((p) => p.id === choice.id)
  if (!entry) {
    const ready = catalog.filter((p) => p.status === 'ready').map((p) => p.id)
    const planned = catalog.filter((p) => p.status === 'planned').map((p) => p.id)
    const hint = planned.length ? ` (planned, not yet implemented: ${planned.join(', ')})` : ''
    return { ok: false, errors: [`provider.id: unknown provider "${choice.id}" — available: ${ready.join(', ')}${hint}`] }
  }
  if (entry.status === 'planned') {
    return {
      ok: false,
      errors: [`provider.id: "${entry.id}" is a declared but unimplemented provider — see apps/voicebox/docs/PROVIDER_API.md`],
    }
  }
  // Fail here, loudly, rather than at connect time as a mic that never answers.
  if (entry.configured === false) {
    return { ok: false, errors: [`provider.id: ${missingKeyMessage(entry)}`] }
  }
  if (choice.placement && !entry.placements.includes(choice.placement)) {
    return {
      ok: false,
      errors: [
        `provider.placement: "${choice.placement}" is not supported by "${entry.id}" — supported: ${entry.placements.join(', ')}`,
      ],
    }
  }
  return { ok: true, value: { id: entry.id, placement: choice.placement ?? entry.placements[0] } }
}

/** Names the exact secret and both places it can be set. Callers paste this at people. */
function missingKeyMessage(entry: ProviderCatalogEntry): string {
  const missing = entry.missingSecrets?.length ? entry.missingSecrets : ['its API key']
  const plural = missing.length > 1 ? 's' : ''
  return (
    `"${entry.id}" is installed but not configured — missing Worker secret${plural} ${missing.join(', ')}. ` +
    `Set ${missing.map((s) => DEPLOY_SECRET_PREFIX + s).join(', ')} as a GitHub secret and re-run ` +
    `deploy-voicebox-server.yml, or run ${missing.map((s) => `\`wrangler secret put ${s}\``).join(' and ')}`
  )
}

const CONFIG_FIELDS = [
  'persona',
  'provider',
  'voice',
  'locale',
  'turnTaking',
  'greeting',
  'tools',
  'webhook',
  'deepThink',
  'audio',
  'maxDurationMs',
  'ref',
] as const

/**
 * Validates and normalises a caller's session config. Every optional field comes
 * back filled in, so the server never has to re-derive a default.
 */
export function validateSessionConfig(input: unknown, catalog: ProviderCatalogEntry[]): Validated<SessionConfig> {
  const errors: string[] = []
  if (!isRecord(input)) return { ok: false, errors: ['body: expected a JSON object'] }

  for (const key of Object.keys(input)) {
    if (!(CONFIG_FIELDS as readonly string[]).includes(key)) {
      errors.push(`unknown field "${key}" — allowed: ${CONFIG_FIELDS.join(', ')}`)
    }
  }

  const persona = input.persona
  if (typeof persona !== 'string' || !persona.trim()) errors.push('persona: required, a non-empty string')
  else if (persona.length > MAX_PERSONA) errors.push(`persona: too long (${persona.length} > ${MAX_PERSONA})`)

  let provider: ResolvedProvider | null = null
  if (!isRecord(input.provider) || typeof input.provider.id !== 'string') {
    errors.push('provider: required, e.g. { "id": "openai-realtime" }')
  } else {
    const choice = input.provider as unknown as ProviderChoice
    const options = choice.options ?? {}
    if (choice.options !== undefined && !isRecord(choice.options)) errors.push('provider.options: expected an object')
    const resolved = resolveProvider(choice, catalog)
    if (resolved.ok) provider = { ...resolved.value, options }
    else errors.push(...resolved.errors)
  }

  const turnTaking = input.turnTaking ?? DEFAULTS.turnTaking
  if (turnTaking !== 'vad' && turnTaking !== 'ptt') errors.push('turnTaking: expected "vad" or "ptt"')

  for (const key of ['voice', 'locale', 'greeting', 'ref'] as const) {
    if (input[key] !== undefined && typeof input[key] !== 'string') errors.push(`${key}: expected a string`)
  }

  const maxDurationMs = input.maxDurationMs ?? DEFAULTS.maxDurationMs
  if (typeof maxDurationMs !== 'number' || maxDurationMs <= 0 || maxDurationMs > 4 * 3600_000) {
    errors.push('maxDurationMs: expected a positive number up to 14400000 (4h)')
  }

  // Audio defaults come from the chosen provider, so switching to one with a different
  // native sample rate stays a config field rather than a code edit.
  const audio = validateAudio(input.audio, errors, catalog.find((p) => p.id === provider?.id)?.audio)
  const tools = validateTools(input.tools, errors)
  const webhook = validateWebhook(input.webhook, errors)
  const deepThink = validateDeepThink(input.deepThink, errors)

  if (errors.length || !provider) return { ok: false, errors }

  return {
    ok: true,
    value: {
      persona: (persona as string).trim(),
      provider,
      voice: input.voice as string | undefined,
      locale: input.locale as string | undefined,
      turnTaking: turnTaking as TurnTaking,
      greeting: input.greeting as string | undefined,
      tools,
      webhook,
      deepThink,
      audio,
      maxDurationMs: maxDurationMs as number,
      ref: input.ref as string | undefined,
    },
  }
}

/** Strips caller secrets so a config can be logged or echoed back safely. */
export function redactConfig(config: SessionConfig): SessionConfig {
  if (!config.webhook?.secret) return config
  return { ...config, webhook: { ...config.webhook, secret: '[redacted]' } }
}

function validateAudio(raw: unknown, errors: string[], native?: SessionConfig['audio']): SessionConfig['audio'] {
  const out = { input: native?.input ?? DEFAULT_AUDIO, output: native?.output ?? DEFAULT_AUDIO }
  if (raw === undefined) return out
  if (!isRecord(raw)) {
    errors.push('audio: expected { input?, output? }')
    return out
  }
  for (const dir of ['input', 'output'] as const) {
    const f = raw[dir]
    if (f === undefined) continue
    if (!isRecord(f) || typeof f.sampleRate !== 'number' || typeof f.channels !== 'number') {
      errors.push(`audio.${dir}: expected { encoding, sampleRate, channels }`)
      continue
    }
    if (f.encoding !== 'pcm16' && f.encoding !== 'opus' && f.encoding !== 'mulaw') {
      errors.push(`audio.${dir}.encoding: expected "pcm16" | "opus" | "mulaw"`)
      continue
    }
    out[dir] = { encoding: f.encoding, sampleRate: f.sampleRate, channels: f.channels }
  }
  return out
}

function validateTools(raw: unknown, errors: string[]): ToolDecl[] {
  if (raw === undefined) return []
  if (!Array.isArray(raw)) {
    errors.push('tools: expected an array')
    return []
  }
  if (raw.length > MAX_TOOLS) errors.push(`tools: too many (${raw.length} > ${MAX_TOOLS})`)

  const out: ToolDecl[] = []
  const seen = new Set<string>()
  raw.slice(0, MAX_TOOLS).forEach((t, i) => {
    const at = `tools[${i}]`
    if (!isRecord(t) || typeof t.name !== 'string' || !/^[a-zA-Z0-9_-]{1,64}$/.test(t.name)) {
      errors.push(`${at}.name: required, [a-zA-Z0-9_-]{1,64}`)
      return
    }
    if (seen.has(t.name)) {
      errors.push(`${at}.name: duplicate tool "${t.name}"`)
      return
    }
    seen.add(t.name)

    const exec = validateExec(t.exec, `${at}.exec`, errors)
    if (!exec) return

    const builtin = exec.kind === 'builtin' ? BUILTIN_TOOLS[t.name] : undefined
    if (exec.kind === 'builtin' && !builtin) {
      errors.push(`${at}: no builtin tool named "${t.name}" — available: ${Object.keys(BUILTIN_TOOLS).join(', ')}`)
      return
    }
    // Builtins own their own contract; callers may omit description/parameters.
    const description = typeof t.description === 'string' ? t.description : builtin?.description
    const parameters = isRecord(t.parameters) ? (t.parameters as JsonSchemaObject) : builtin?.parameters
    if (!description) errors.push(`${at}.description: required`)
    if (!parameters || parameters.type !== 'object') errors.push(`${at}.parameters: required, a JSON Schema of type "object"`)

    const timeoutMs = t.timeoutMs ?? (exec.kind === 'builtin' ? DEFAULTS.deepThinkTimeoutMs : DEFAULTS.toolTimeoutMs)
    if (typeof timeoutMs !== 'number' || timeoutMs <= 0 || timeoutMs > 120_000) {
      errors.push(`${at}.timeoutMs: expected a positive number up to 120000`)
    }
    const after = t.after ?? 'continue'
    if (after !== 'continue' && after !== 'stop') errors.push(`${at}.after: expected "continue" or "stop"`)
    if (!description || !parameters) return
    out.push({ name: t.name, description, parameters, exec, timeoutMs: timeoutMs as number, after: after as ToolAfter })
  })
  return out
}

function validateExec(raw: unknown, at: string, errors: string[]): ToolExec | null {
  if (!isRecord(raw) || typeof raw.kind !== 'string') {
    errors.push(`${at}: required, one of { kind: "builtin" | "webhook" | "client" }`)
    return null
  }
  if (raw.kind === 'builtin' || raw.kind === 'client') return { kind: raw.kind }
  if (raw.kind === 'webhook') {
    if (typeof raw.url !== 'string' || !isCallbackUrl(raw.url)) {
      errors.push(`${at}.url: required, an https:// URL (http:// allowed for localhost only)`)
      return null
    }
    return { kind: 'webhook', url: raw.url }
  }
  errors.push(`${at}.kind: expected "builtin" | "webhook" | "client", got "${raw.kind}"`)
  return null
}

function validateWebhook(raw: unknown, errors: string[]): WebhookConfig | undefined {
  if (raw === undefined) return undefined
  if (!isRecord(raw) || typeof raw.url !== 'string' || !isCallbackUrl(raw.url)) {
    errors.push('webhook.url: required, an https:// URL (http:// allowed for localhost only)')
    return undefined
  }
  let events: WebhookEventKind[] = ['transcript', 'tool', 'session']
  if (raw.events !== undefined) {
    if (!Array.isArray(raw.events) || raw.events.some((e) => !['transcript', 'tool', 'session'].includes(e as string))) {
      errors.push('webhook.events: expected a subset of ["transcript","tool","session"]')
    } else {
      events = raw.events as WebhookEventKind[]
    }
  }
  if (raw.secret !== undefined && typeof raw.secret !== 'string') errors.push('webhook.secret: expected a string')
  return { url: raw.url, secret: raw.secret as string | undefined, events }
}

function validateDeepThink(raw: unknown, errors: string[]): DeepThinkConfig {
  const out: DeepThinkConfig = { timeoutMs: DEFAULTS.deepThinkTimeoutMs }
  if (raw === undefined) return out
  if (!isRecord(raw)) {
    errors.push('deepThink: expected an object')
    return out
  }
  for (const key of ['baseUrl', 'model', 'systemPrompt'] as const) {
    if (raw[key] === undefined) continue
    if (typeof raw[key] !== 'string') errors.push(`deepThink.${key}: expected a string`)
    else out[key] = raw[key] as string
  }
  if (raw.timeoutMs !== undefined) {
    if (typeof raw.timeoutMs !== 'number' || raw.timeoutMs <= 0 || raw.timeoutMs > 120_000) {
      errors.push('deepThink.timeoutMs: expected a positive number up to 120000')
    } else {
      out.timeoutMs = raw.timeoutMs
    }
  }
  return out
}

/** HTTPS everywhere, except loopback so `wrangler dev` can call a local stub. */
function isCallbackUrl(raw: string): boolean {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return false
  }
  if (url.protocol === 'https:') return true
  return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
